<template>
  <b-card
    no-body
    v-draggable
    ref="survey_subcard"
    id="survey-subcard"
  >
    <!-- Header -->
    <b-card-header
      style="color: lightgrey; height: 25px; cursor: move"
      class="pl-2 pb-0 pt-0"
      header-tag="div"
      header-bg-variant="dark"
    >
      <b-row class="mx-0 px-0">
        <b-col class="mx-0 px-0" cols="11">
          {{ HeadLine }}
        </b-col>
        <b-col class="mx-0 px-0" cols="1" style="text-align: right">
          <font-awesome-icon
            class="mx-1 clickable"
            :icon="['far', 'times']"
            @click="close()"
          />
        </b-col>
      </b-row>
    </b-card-header>

    <!-- Content -->
    <div v-if="isLoading" :class="{ spinner: isLoading }" style="width:700px; height: 100px;"></div>
    <b-card-body class="pl-3 pr-3 pt-0" style="display: flex; overflow: auto" v-else-if="SurveyData != null">
      <div style="width:700px; overflow-y:auto; overflow-x:hidden">
        <!-- Top menu -->
        <b-row align-h="between" align-v="center">
          <b-col cols="8" class="subcard-title">
            {{ HeadLine }} - {{ surveyData.SurveyTitle}}
          </b-col>
          <b-col cols="4" style="text-align: end;">
            <IntermediaryImage :OverrideText="SurveyData.OverrideText" v-if="SurveyData.OverrideText" />
            <img class="module-logo mx-2 my-1" :src="LogoUrl" v-else/>
          </b-col>
        </b-row>

        <!-- OverallScore -->
        <b-row class="mt-2">
          <b-col>
            <h5 class="mb-4">{{$t('Webportal.CX.Exploration.OverallScore')}}</h5>
            <DxChart
              :data-source="SurveyData.WaveResults"
              :size="{height: 150, width: 650}"
              class="mb-5"
            >
              <DxSeries
                argument-field="WaveDate"
                value-field="Result"
                type="line"
                name="OverallScore"
                :color="getSeriesColor()"
              />
              <DxValueAxis
                :visual-range="[0, 100 + 100 * 0.1]"
              />
              <DxLegend
                vertical-alignment="bottom"
                horizontal-alignment="center"
                item-text-position="bottom"
                :font="{size: 14, weight: 600, color: 'black'}"
                :visible="false"
              />
              <DxTooltip 
                :enabled="true"
                :customize-tooltip="customizeTooltip"
              />
            </DxChart>
            <hr>
          </b-col>
        </b-row>

        <!-- crucialClusters -->
        <b-row class="mt-2">
          <table class="table m-3" v-if="crucialClusters">
            <tr >
              <th class="border-top-0">{{ $t("Webportal.CX.Explorations.RatingsLabel") }}</th>
              <th class="border-top-0" colspan="3">{{ $t("Webportal.CX.Dashboard.Exploration.History") }}</th>
              <th class="border-top-0" colspan="2">{{ $t("Webportal.CX.Dashboard.Exploration.CurrentValue") }}</th>
            </tr>
            <ClusterTableRow
              v-for="(cluster, index) in crucialClusters"
              :key="index"
              :Title="cluster.GridText"
              :Waves="cluster.Waves" 
              valueSymbol="%"
              :isSelected="cluster.GridText == selectedCluster"
              @click="toggleSurveyInstanceDataPreview"
            />
          </table>
        </b-row>
      </div>
      
      <!-- SurveyInstance Preview -->
      <div class="ml-2 pl-2 mt-2" style="width:700px; overflow:auto; border-left: 1px solid lightgray" v-if="selectedCluster">
        <div v-if="isLoadingSurveyInstanceDataSet" :class="{ spinner: isLoadingSurveyInstanceDataSet }" style="height:100%"></div>
        <b-tabs v-else>
          <!-- SurveyInstance -->
          <template v-for="(SurveyInstanceDataSet, index) in SurveyInstanceDataSets">
            <b-tab :title="index == 0 ? $t('Webportal.CX.Dashboard.CurrentCheck') : $t('Webportal.CX.Dashboard.PreviousCheck') + ' ' + index" :key="index" style="height:600px; overflow:auto">
              <!-- Section -->
              <div v-for="SurveySection in SurveyInstanceDataSet.surveySection" :key="SurveySection.surveySectionId">
                <!-- SectionGrid -->
                <div v-for="SurveySectionGrid in SurveyInstanceDataSet.surveySectionGrid" :key="SurveySectionGrid.surveySectionGridId">
                  <template v-if="SurveySectionGrid.surveySectionId == SurveySection.surveySectionId && SurveySectionGrid.gridText == selectedCluster && SurveySectionGrid.isCluster">
                    <b-row style="font-weight:bolder">
                      <b-col v-if="SurveySectionGrid.pointsReached != null && SurveySectionGrid.pointsMax" cols="4">
                        <span>{{$t('Webportal.CX.AllPointsAchieved')}}</span>                        
                      </b-col>
                      <b-col :cols="(SurveySectionGrid.pointsReached != null && SurveySectionGrid.pointsMax) ? 5 : 9">{{SurveySectionGrid.gridText}}</b-col>
                      <b-col cols="3">
                        <b-button 
                          @click="downloadCheckReport(SurveyInstanceDataSet.surveyInstance[0].surveyInstanceId)"
                          :disabled="isDownloading"
                          >
                          <font-awesome-icon icon="file-pdf" /> {{ $t("Webportal.Download") }}
                        </b-button>
                      </b-col>
                      <!-- <b-col cols="2" v-if="SurveySectionGrid.pointsReached != null && SurveySectionGrid.pointsMax">{{SurveySectionGrid.pointsReached}}/{{SurveySectionGrid.pointsMax}}</b-col> -->
                    </b-row>
                    <b-row v-if="SurveySectionGrid.pointsReached != null && SurveySectionGrid.pointsMax">
                      <b-col cols="4">
                        <!-- Doughnut -->
                        <Doughnut :green="SurveySection.positiveQuestions" :red="SurveySection.negativeQuestions"/>
                      </b-col>
                      <b-col cols="8">
                        <!-- Cluster Note -->
                        <template v-for="Cluster in SurveyClusters">
                          <b-row v-if="Cluster.GridText == SurveySectionGrid.gridText" :key="Cluster.GridText">
                            <template v-for="Wave in Cluster.Waves" >
                              <div v-if="Wave.Name == SurveyInstanceDataSet.surveyInstance[0]['surveyWave.Name']" :key="Wave.Name">
                                <p class="px-3" v-html="Wave.Hints"></p>
                              </div>
                            </template>
                          </b-row>
                        </template>
                      </b-col>
                    </b-row>
                    <hr style="width:100%">
                    <!-- preview table -->
                    <template v-if="sectionHasQuestion(SurveySection.surveySectionId, SurveyInstanceDataSet.surveyQuestion)">
                      <!-- Headers -->
                      <b-row style="font-weight:bolder">
                        <b-col cols="5">{{$t('Webportal.Question')}}</b-col>
                        <b-col cols="3">{{$t('Webportal.Answer')}}</b-col>
                        <b-col cols="2" style="text-align:center">
                          {{$t('Webportal.CX.AllPointsAchieved')}}
                        </b-col>
                        <b-col cols="2" style="text-align:right">{{$t('Webportal.Points')}}</b-col>
                      </b-row>
                      <hr>
                      <!-- Question -->
                      <template v-for="SurveyQuestion in SurveyInstanceDataSet.surveyQuestion">
                        <b-row v-if="SurveyQuestion.surveySectionId == SurveySection.surveySectionId" :key="SurveyQuestion.surveyQuestionId">
                          <b-col cols="5" style="">{{SurveyQuestion.questionNumber}} {{SurveyQuestion.questionText}}</b-col>
                          <!-- Comment -->
                          <template v-for="(SurveyComment, key) in SurveyInstanceDataSet.surveyComment">
                            <div v-if="SurveyComment.surveyQuestionId == SurveyQuestion.surveyQuestionId" :key="key">
                              <p style="padding-left:25px">{{SurveyComment.commentByShopper}}</p>
                            </div>
                          </template>
                          <!-- upload -->
                          <b-col cols="7" v-if="SurveyQuestion.isPictureUploadQuestion">
                            <template v-for="(SurveyPictureUpload, key) in SurveyInstanceDataSet.surveyPictureUpload">
                              <div class="mt-1" v-if="SurveyPictureUpload.surveyQuestionId == SurveyQuestion.surveyQuestionId" :key="key">
                                <div v-if="!SurveyPictureUpload.loadedPicture"  :class="{ spinner: !SurveyPictureUpload.loadedPicture }"></div>
                                <b-img 
                                  class="upload-picture clickable" 
                                  v-else-if="SurveyPictureUpload.pictureUpload" 
                                  :src="SurveyPictureUpload.pictureUpload" 
                                  @click="toggleBigPicture(SurveyPictureUpload.pictureUpload)"
                                ></b-img>
                              </div>
                            </template>
                          </b-col>
                          <!-- Answer -->
                          <b-col cols="3">
                            <template v-for="SurveyAnswer in SurveyInstanceDataSet.surveyAnswer">
                              <div v-if="SurveyAnswer.surveyQuestionId == SurveyQuestion.surveyQuestionId" :key="SurveyAnswer.surveyAnswerId">
                                <b-row>
                                  <b-col cols="2" class="p-0">
                                    <template v-if="SurveyQuestion.isMultipleSelection">
                                      <font-awesome-icon v-if="SurveyAnswer.isAnswerSelected" :icon="['far', 'times-square']" />
                                      <font-awesome-icon v-else :icon="['far', 'square']" />
                                    </template>
                                    <template v-else>
                                      <font-awesome-icon v-if="SurveyAnswer.isAnswerSelected" :icon="['far', 'dot-circle']" />
                                      <font-awesome-icon v-else :icon="['far', 'circle']" />
                                    </template>
                                  </b-col>
                                  <b-col cols="10" class="p-0">
                                    <span>{{SurveyAnswer.surveyAnswerText}}</span>
                                  </b-col>
                                </b-row>
                              </div>
                            </template>
                          </b-col>
                          <!-- check or times -->
                          <b-col cols="2" v-if="SurveyQuestion.maximumPoints" style="text-align:center">
                            <font-awesome-icon v-if="SurveyQuestion.pointsReached != SurveyQuestion.maximumPoints"  :icon="['far', 'times']" style="color:red" class="text-danger"/>
                            <font-awesome-icon v-else :icon="['far', 'check']" style="color:green" class="text-success"/>
                          </b-col>
                          <!-- QuestionPoints -->
                          <b-col cols="2" v-if="SurveyQuestion.pointsReached != null && SurveyQuestion.maximumPoints" style="text-align:right">{{SurveyQuestion.pointsReached}}/{{SurveyQuestion.maximumPoints}}</b-col>
                          <hr style="width:100%">
                        </b-row>
                      </template>
                    </template>
                    <p v-else>
                      {{$t('Webportal.CX.EmptyClusterPreview')}}
                    </p>
                  </template>
                </div>
              </div>
            </b-tab>
        </template>
      </b-tabs>
      </div>

    </b-card-body>

    <!-- N / A -->
    <b-card-text class="pl-3 pr-3" style="overflow: auto" v-else>
      <span v-html="$t('Webportal.CX.Dashboard.Exploration.Detailview.NoData')"></span>
    </b-card-text>
    <!-- big picture -->
    <div class="big-picture-wrapper clickable" v-if="showBigPicture" @click="toggleBigPicture(null)">
      <b-img class="big-picture clickable" :src="bigPicture"></b-img>
    </div>
  </b-card>
</template>

<script>
import DashboardService from "@/services/DashboardService";
import CancelToken from '@/mixins/CancelToken';
import ClusterTableRow from "@/components/CX/ExplorationCard/ClusterTableRow";
import DownloadFileMixin from '@/mixins/DownloadFileMixin'
import { DxChart, DxSeries, DxTooltip, DxLegend, DxValueAxis } from 'devextreme-vue/chart';
import ReportsService from '@/services/ReportsService';
import { i18n } from "@/i18n";
import IntermediaryImage from "@/components/CX/ExplorationCard/IntermediaryImage";
import Doughnut from './Doughnut.vue';

export default {
  name: "ExplorationSurveyPopup",

  components: {
    ClusterTableRow,
    DxChart,
    DxSeries,
    DxValueAxis,
    DxLegend,
    DxTooltip,
    IntermediaryImage,
    Doughnut
  },

  mixins: [ DownloadFileMixin, CancelToken ],

  props: {
    surveyData: {
      type: Object,
      default: () => {},
    },
    clientLocationId: Number,
    headLine: String,
  },

  data() {
    return {
      SurveyData: null,
      SurveyClusters: null,
      isLoading: true,
      isLoadingSurveyInstanceDataSet: true,
      cancelToken: null,
      isDownloading: false,
      SurveyInstanceDataSets: null,
      showSurveyInstanceData: false,
      selectedCluster: null,
      bigPicture: null,
      showBigPicture: false
    };
  },

  watch: {
    SurveyData: {
      immediate: true,
      handler() {
        if(this.SurveyData) {
          this.SurveyData?.WaveResults?.forEach(result => {
            result.WaveDate = new Date(result.WaveDate);
          }) 
        }
      }
    }
  },

  computed: {
    crucialClusters: function() {
      // Items without a value are always fulfilled
      return this.SurveyClusters?.filter(c => c.Waves?.[0]?.PointsMax);
    },
    LogoUrl: function() {
      let imgId = this.SurveyData?.ExtLogoId;
      return imgId ? `${process.env.BASE_URL}api/media/image?id=${imgId}` : null;
    },
    HeadLine: function() {
      return this.headLine || this.SurveyData?.Name || this.$t('Webportal.CX.SurveyDetails');
    }
  },

  created: function() {
    this.SurveyData = { ...this.surveyData };
    this.AcquireData();
    window.addEventListener('keyup', this.handleKeyUp);
  },

  mounted: function () {
    var surveyInstanceIds = [];
    this.isLoadingSurveyInstanceDataSet = true;
    this.SurveyData?.SurveyInstancesOrderedByDateDesc.forEach(si => {
      surveyInstanceIds.push(si.SurveyInstanceId);
    });
    DashboardService.GetSurveyInstanceDataSet(surveyInstanceIds.toString(), i18n.locale)
    .then(res => {
      res.data.forEach(surveyInstanceDataSet => {

        // insert upload pictures
        surveyInstanceDataSet.surveyPictureUpload.forEach(spu => {
          this.getImage(spu);
        });

        // count positive and negative questions per section
        surveyInstanceDataSet.surveySection.forEach(sec => {
          sec.positiveQuestions = 0;
          sec.negativeQuestions = 0;
          surveyInstanceDataSet.surveyQuestion.forEach(sq => {
            if(sq.surveySectionId != sec.surveySectionId || !sq.maximumPoints) return;
            if(sq.maximumPoints == sq.pointsReached) {
              sec.positiveQuestions++;
            } else {
              sec.negativeQuestions++;
            }
          });
        });

        this.SurveyInstanceDataSets = res.data;
      });
    })
    .finally(() => {
      this.isLoadingSurveyInstanceDataSet = false;
    }) 
    .catch(err => {
      console.error(err);
    })
  },

  methods: {
    AcquireData: function() {
      this.isLoading = true;
      this.cancelToken = this.initCancelToken(this.cancelToken);

      DashboardService
        .getSurveyData(
          {
            externalSurveyId: this.SurveyData.ExternalSurveyId,
            clientLocationId: this.clientLocationId
          },
          this.cancelToken
        )
        .then(response => {
          // #1157 - Empty clusters are considered "positive"
          this.SurveyClusters = response.data;
          this.SurveyClusters.forEach(cluster => {
            cluster.Waves?.forEach(wave => {
              wave.Hints = (wave.Hints || '').replaceAll('|||', '<hr>');
            })
          });

          // initially set first Cluster with pointsMax as selected
          this.selectedCluster = this.SurveyClusters?.find(c => c.Waves?.[0]?.PointsMax).GridText;
        })
        .catch(error => console.error('Failed to acquire survey data.', error))
        .finally(() => {
          this.isLoading = false;
          this.cancelToken = null;
        });
    },
    getColor(value) {
      return this.$parent.getColor(value);
    },
    customizeTooltip(arg) {
      const result = this.SurveyData.WaveResults.find(x => x.WaveDate.getTime() == arg.argument.getTime());
      const date = new Date(result.WaveDate).toLocaleString('default', {month: 'short', year: 'numeric'});
      return { text: `${arg.value}% - ${date} | ${this.$t('Webportal.Wave')}: ${result.WaveName} ` };
    },
    downloadCheckReport(surveyInstanceId) {
      this.isDownloading = true;
      ReportsService.getCheckReport(
        surveyInstanceId,
        { localizationId: i18n.locale })
        .then(response => {
          this.downloadFile(response);
          this.isDownloading = false;
        })
        .catch(_ => { 
          this.isDownloading = false; 
          this.$bvToast.toast(
            this.$t("Webportal.CX.Margin.CheckReportErrorMessage"),
            {
              title: this.$t("Webportal.CX.Margin.CheckReportErrorTitle"),
              autoHideDelay: 5000,
              variant: "danger",
              noCloseButton: true,
            }
          );
        } 
      );
    },
    getSeriesColor() {
      var theme = localStorage.getItem("user-theme");
      if(theme == 'dark') return 'white';
      if(theme == 'light') return 'black';
    },
    toggleSurveyInstanceDataPreview(clusterTitle) {
      if(this.selectedCluster != clusterTitle) {
        this.selectedCluster = clusterTitle;
      } else {
        this.selectedCluster = null;
      }
    },
    async getImage(surveyPictureUpload) {
      surveyPictureUpload.loadedPicture = false;
      await DashboardService.getSurveyUploadFile(surveyPictureUpload.surveyInstanceId ,surveyPictureUpload.surveyQuestionId, surveyPictureUpload.fileName)
      .then((res) => {
        if(res.status == 200) {
          surveyPictureUpload.pictureUpload = window.URL.createObjectURL(new Blob([res.data]));
        } else if(res.status == 204) {
          // no content
          surveyPictureUpload.pictureUpload = window.URL.createObjectURL(new Blob([{}]));
        }
      })
      .finally(() => {
        surveyPictureUpload.loadedPicture = true;
        return;
      })
    },
    toggleBigPicture(picture) {
      this.bigPicture = picture;
      this.showBigPicture = !this.showBigPicture;
    },
    close() {
      window.removeEventListener('keyup', this.handleKeyUp);
      this.$emit('close');
    },
    handleKeyUp(event) {
      if (event.key == 'Escape') {
        this.close();
      }
    },
    sectionHasQuestion(surveySectionId, surveyQuestionArray) {
      var res = false;
      surveyQuestionArray.forEach(sq => {
        if(sq.surveySectionId == surveySectionId) {
          res = true;
          return;
        }
      });
      return res;
    }
  },
};
</script>

<style scoped>
.subcard-title {
  font-size: 1.5rem;
}
.module-logo {
  max-width: 185px;
  max-height: 50px;
}
#survey-subcard {
  position: fixed !important;
  /* width: 700px; */
}
.upload-picture {
  width: 50%;
  border: 1px solid var(--border-color-secondary);
  border-radius: var(--border-radius-popup);
}
.big-picture {
  position: fixed;
  top: 50%;
  left: 50%;
  max-width: 90vw;
  max-height: 90vh;
  transform: translateX(-50%) translateY(-50%);
  box-shadow: 3px 3px 13px -3px var(--border-color-secondary);
  border: 1px solid var(--border-color-secondary);
  border-radius: var(--border-radius-popup);
}
.big-picture-wrapper {
  position: fixed;
  top: 0%;
  left: 0%;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
}
</style>
